import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  InputAdornment,
  Button,
  Skeleton,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';
import { getCategoriesForShop } from '../utils/categories';

const CustomerCatalog = ({ shopName }) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const predefinedCategories = getCategoriesForShop(shopName);
  
  // Since we aren't fetching all items, we can only show predefined categories in the dropdown.
  const allCategories = predefinedCategories;

  const loadItems = useCallback(async (isLoadMore = false) => {
    try {
      setLoading(true);
      const limit = 12; // Load 12 items at a time
      
      const { items: fetchedItems, lastDoc: newLastDoc } = await inventoryService.getPaginatedItems(
        shopName, 
        limit, 
        isLoadMore ? lastDoc : null, 
        selectedCategory
      );
      
      if (fetchedItems.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setLastDoc(newLastDoc);

      if (isLoadMore) {
        setItems(prev => [...prev, ...fetchedItems]);
        setFilteredItems(prev => {
          const newItems = [...prev, ...fetchedItems];
          if (searchTerm) {
            return newItems.filter(item => 
              item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              item.brand.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          return newItems;
        });
      } else {
        setItems(fetchedItems);
        if (searchTerm) {
          setFilteredItems(fetchedItems.filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.brand.toLowerCase().includes(searchTerm.toLowerCase())
          ));
        } else {
          setFilteredItems(fetchedItems);
        }
      }
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  }, [shopName, selectedCategory, lastDoc, searchTerm]);

  // Load initial items when category changes
  useEffect(() => {
    setLastDoc(null);
    setHasMore(true);
    setItems([]);
    setFilteredItems([]);
    // We can't safely call loadItems here because it depends on lastDoc. 
    // We'll use a separate effect for initial load.
  }, [selectedCategory]);

  useEffect(() => {
    if (items.length === 0 && hasMore) {
      loadItems(false);
    }
  }, [items.length, hasMore, loadItems]);

  useEffect(() => {
    let filtered = items;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(term) ||
        item.brand.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    setFilteredItems(filtered);
  }, [items, searchTerm, selectedCategory]);

  if (loading && items.length === 0) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        {/* Header Skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 3, sm: 4 } }}>
          <Skeleton variant="rounded" width={40} height={40} />
          <Box>
            <Skeleton variant="text" width={200} height={32} />
            <Skeleton variant="text" width={140} height={20} />
          </Box>
        </Box>
        
        {/* Filters Skeleton */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Skeleton variant="rounded" sx={{ minWidth: { xs: '100%', sm: 280 } }} height={40} />
          <Skeleton variant="rounded" sx={{ minWidth: { xs: '100%', sm: 180 } }} height={40} />
        </Box>

        {/* Grid Skeleton */}
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Card sx={{ height: '100%', borderRadius: 1.5, border: '1px solid #282D33' }}>
                <Skeleton variant="rectangular" width="100%" height={160} />
                <CardContent sx={{ p: 3 }}>
                  <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="text" width="50%" height={32} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        mb: { xs: 2.5, sm: 3 },
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1,
          background: '#E13B36',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <StorefrontIcon sx={{ fontSize: 22, color: 'white' }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{
            fontWeight: 800, color: '#F5F5F5',
            fontSize: { xs: '1.15rem', sm: '1.5rem' },
            letterSpacing: '-0.02em',
          }}>
            {shopName} Product Catalog
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
            Browse available products
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{
        display: 'flex', gap: { xs: 1.5, sm: 2 },
        mb: { xs: 3, sm: 4 },
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          label="Search products..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
              </InputAdornment>
            ),
            ...{ sx: { fontSize: { xs: '16px', sm: '0.875rem' } } }
          }}
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
        />

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, '& .MuiSelect-select': { fontSize: { xs: '16px', sm: '0.875rem' } } }}>
          <InputLabel sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="">All Categories</MenuItem>
            {allCategories.map(category => (
              <MenuItem key={category} value={category}>{category}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Product Grid */}
      {filteredItems.length > 0 ? (
        <Grid container spacing={2}>
          {filteredItems.map(item => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                transition: 'border-color 0.18s, background-color 0.18s',
                borderRadius: 1.5,
                border: '1px solid #282D33',
                '&:hover': { borderColor: '#464D55', bgcolor: '#181C21' }
              }}>
                <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'center', bgcolor: '#101317', borderBottom: '1px solid #282D33' }}>
                  <img src="/default_tire.png" alt="Tire" style={{ width: 140, height: 140, objectFit: 'contain', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.15))' }} />
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {item.name}
                    </Typography>
                    {item.quantity > 0 ? (
                      <Chip size="small" label="In Stock" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(54,162,105,0.12)', color: '#58C185', border: '1px solid rgba(54,162,105,0.28)' }} />
                    ) : (
                      <Chip size="small" label="Out of Stock" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.06)', color: '#9E9E9E', border: '1px solid rgba(255,255,255,0.1)' }} />
                    )}
                  </Box>

                  {item.brand && (
                    <Typography variant="caption" sx={{ color: '#BDBDBD', display: 'block', mb: 1 }}>
                      Brand: {item.brand}
                    </Typography>
                  )}

                  <Typography variant="body2" sx={{ color: '#BDBDBD', mb: 2, flexGrow: 1 }}>
                    {item.description || 'No description available.'}
                  </Typography>

                  <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {item.sellingPrice > 0 ? (
                      <Typography variant="h6" sx={{ color: '#E53935', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalOfferIcon sx={{ fontSize: 18 }} />
                        ₱{item.sellingPrice.toFixed(2)}
                      </Typography>
                    ) : (
                      <Typography variant="subtitle2" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                        {item.quantity > 0 ? 'Ask the shop/owner for price' : 'Price unavailable'}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed #3A4047', borderRadius: 1.5 }}>
          <StorefrontIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="body1" sx={{ color: '#BDBDBD', fontWeight: 500 }}>
            {searchTerm || selectedCategory ? 'No products match your search.' : 'No products available.'}
          </Typography>
        </Paper>
      )}

      {hasMore && filteredItems.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => loadItems(true)}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ px: 4, py: 1.5, borderRadius: 1.5, fontWeight: 700, minHeight: 44 }}
          >
            {loading ? 'Loading...' : 'Load More Products'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CustomerCatalog;
